/**
 * Names of staff (D57, roles-directory-names). The server returns a name only when the role holds `staff.see_names`
 * (or `staff.see_trainer_names` for a trainer); otherwise null. The screen then shows a neutral label and never a
 * fragment of the id.
 */
export const NAME_HIDDEN = {
  colleague: "Staff member",
  trainer: "Trainer",
  you: "You",
} as const;

export type NameKind = "staff" | "trainer";

/**
 * The text for one person. Order: the person is you (your own name from `/me`, else "You"), then a name the server
 * sent (from `/directory` or from the record itself), then the neutral label for what they are.
 */
export function staffLabel(o: { id: string; myId?: string | null; myName?: string | null; name?: string | null; kind?: NameKind; isTrainer?: boolean; ordinal?: number | null }): string {
  if (o.myId && o.id === o.myId) return o.myName || NAME_HIDDEN.you;
  if (o.name) return o.name;
  const base = o.kind === "trainer" || o.isTrainer ? NAME_HIDDEN.trainer : NAME_HIDDEN.colleague;
  return o.ordinal ? `${base} ${o.ordinal}` : base;
}

/**
 * Founder decision 2026-11-04: hidden people are numbered ("Trainer 1", "Trainer 2", "Staff member 1") so two of them
 * can be told apart. The number is the person's place, by id, among the hidden people of the same kind in the
 * directory. It does not depend on the list on screen, so it is the same on every screen and for every visit. The
 * server still sends `name: null`; the number is made here and carries no id.
 */
export function hiddenOrdinals(entries: ReadonlyArray<{ id: string; name: string | null; isTrainer: boolean }>, myId: string | null): Map<string, number> {
  const out = new Map<string, number>();
  for (const trainer of [true, false]) {
    entries
      .filter((e) => !e.name && e.id !== myId && !!e.isTrainer === trainer)
      .map((e) => e.id)
      .sort()
      .forEach((id, i) => out.set(id, i + 1));
  }
  return out;
}
